import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShieldAlert, Save, ArrowLeft, Plus, Trash2, Send, ImagePlus } from "lucide-react";

const DEFAULT_GENERAL_INSTRUCTIONS = [
  "This is a timed test; the running time is displayed on the top left corner of the screen.",
  "The bar above the question text displays the question numbers in the current section of the test. You can move to any question by clicking on the respective number.",
  "The question screen displays the question number along with the question and respective options.",
  "The top right of the section above the question has an option to mark the question for review. You can later view the marked question.",
  "You can mark or unmark any option you have chosen by tapping on the respective option.",
  "The bottom left corner contains the option to move to the previous question.",
  "The bottom right corner contains the option to move to the next question.",
  "You can jump between sections (if allowed by the tutor) by choosing the section in the bottom centre dropdown.",
  "You can submit the test at any point in time by clicking the Submit button on the top right corner of the screen.",
  "Before submission, the screen shows a confirmation pop-up with: Total number of questions in the test, Questions answered, and Questions marked for review.",
  "The test must be completed in one attempt. Once submitted, it cannot be re-attempted or restarted.",
  "Do not change or close the test screen while attempting the test.",
  "If the app is closed or the screen is changed more than three times, the test will be submitted automatically.",
  "After completion of the test, a summary screen will be displayed with section details and solutions.",
  "If something goes wrong, contact your tutor and communicate the problem."
];

const DEFAULT_SECTIONS = [
  { name: "Everyday Calculation", duration_seconds: 900 },
  { name: "Smart Calculation", duration_seconds: 1200 },
  { name: "Calculation Champion", duration_seconds: 1500 }
];

const DEFAULT_SETTINGS = {
  fullscreen_required: true,
  tab_switch_limit: 3,
  face_detection_enabled: true,
  mic_spike_enabled: true,
  per_question_seconds: 0,
  allow_calculator: false,
  negative_marking: false,
  negative_marks: 0.25,
  randomize_questions: true,
  randomize_options: true,
  allow_section_jump: true
};

export default function SecureExamBuilder() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const editing = !!examId;

  const [exam, setExam] = useState({
    title: "",
    description: "",
    class_ids: [],
    duration_minutes: 60,
    sections: DEFAULT_SECTIONS.map(s => ({ ...s })),
    settings: { ...DEFAULT_SETTINGS },
    questions: [],
    general_instructions_text: DEFAULT_GENERAL_INSTRUCTIONS.join("\n"),
    test_instructions_text: ""
  });
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [cls, ex] = await Promise.all([
          axios.get(`${API}/classes`, { headers }),
          editing ? axios.get(`${API}/secure-exams/${examId}`, { headers }) : Promise.resolve(null)
        ]);
        setClasses(cls.data || []);
        if (ex) setExam({
          ...ex.data,
          settings: { ...DEFAULT_SETTINGS, ...(ex.data.settings || {}) },
          sections: ex.data.sections || DEFAULT_SECTIONS,
          questions: ex.data.questions || [],
          general_instructions_text: (ex.data.general_instructions && ex.data.general_instructions.length > 0 ? ex.data.general_instructions : DEFAULT_GENERAL_INSTRUCTIONS).join("\n"),
          test_instructions_text: (ex.data.test_instructions || []).join("\n")
        });
        const focusQ = searchParams.get("focus");
        if (ex && focusQ) {
          const fq = (ex.data.questions || []).find(q => q.question_id === focusQ);
          if (fq) {
            setEditingQuestion({ ...fq });
            setShowQuestionDialog(true);
            toast.info("Opened the reported question for editing");
          } else {
            toast.error("The reported question no longer exists — it may have been deleted");
          }
        }
      } catch (e) {
        toast.error("Failed to load");
      } finally { setLoading(false); }
    })();
  }, [examId, editing, headers]);

  const saveMeta = async () => {
    if (!exam.title.trim()) { toast.error("Title required"); return; }
    if (exam.sections.length === 0) { toast.error("At least one section required"); return; }
    setSaving(true);
    try {
      const payload = {
        title: exam.title,
        description: exam.description,
        class_ids: exam.class_ids,
        duration_minutes: Number(exam.duration_minutes),
        sections: exam.sections.map(s => ({ name: s.name, duration_seconds: Number(s.duration_seconds) })),
        settings: exam.settings,
        general_instructions: (exam.general_instructions_text || "").split("\n").map(l => l.trim()).filter(Boolean),
        test_instructions: (exam.test_instructions_text || "").split("\n").map(l => l.trim()).filter(Boolean)
      };
      if (editing) {
        const r = await axios.put(`${API}/secure-exams/${examId}`, payload, { headers });
        setExam({ ...exam, ...r.data });
        toast.success("Saved");
      } else {
        const r = await axios.post(`${API}/secure-exams`, payload, { headers });
        toast.success("Exam created — now add questions");
        navigate(`/secure-exams/${r.data.exam_id}/edit`, { replace: true });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const publish = async () => {
    if (!editing) { toast.error("Save the exam first"); return; }
    try {
      await axios.post(`${API}/secure-exams/${examId}/publish`, {}, { headers });
      toast.success("Exam published! Students in assigned classes have been notified.");
      navigate("/secure-exams");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish failed");
    }
  };

  const addSection = () => {
    setExam(e => ({ ...e, sections: [...e.sections, { name: `Section ${e.sections.length + 1}`, duration_seconds: 600 }] }));
  };
  const removeSection = (i) => {
    if (exam.sections.length <= 1) { toast.error("At least one section required"); return; }
    setExam(e => ({ ...e, sections: e.sections.filter((_, idx) => idx !== i) }));
  };
  const updateSection = (i, k, v) => {
    setExam(e => ({ ...e, sections: e.sections.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  };

  const openQDialog = (q = null) => {
    setEditingQuestion(q || {
      section_index: 0,
      type: "mcq",
      text: "",
      image_url: "",
      options: ["", "", "", ""],
      correct_answer: "0",
      numerical_tolerance: 0.01,
      marks: 4
    });
    setShowQuestionDialog(true);
  };

  const saveQuestion = async () => {
    const q = editingQuestion;
    if (!q.text.trim()) { toast.error("Question text required"); return; }
    if (q.type === "mcq" && (!q.options || q.options.filter(o => o.trim()).length < 2)) {
      toast.error("MCQ needs at least 2 options"); return;
    }
    const payload = {
      section_index: Number(q.section_index),
      type: q.type,
      text: q.text,
      image_url: q.image_url || null,
      options: q.type === "mcq" ? q.options.filter((o, i) => i < 6 && (o || "").length >= 0) : null,
      correct_answer: String(q.correct_answer),
      numerical_tolerance: Number(q.numerical_tolerance || 0.01),
      marks: Number(q.marks || 1),
      solution: q.solution || ""
    };
    try {
      let res;
      if (q.question_id) {
        res = await axios.put(`${API}/secure-exams/${examId}/questions/${q.question_id}`, payload, { headers });
        setExam(e => ({ ...e, questions: e.questions.map(x => x.question_id === q.question_id ? res.data : x) }));
      } else {
        res = await axios.post(`${API}/secure-exams/${examId}/questions`, payload, { headers });
        setExam(e => ({ ...e, questions: [...e.questions, res.data] }));
      }
      toast.success("Question saved");
      setShowQuestionDialog(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const deleteQuestion = async (qid) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      await axios.delete(`${API}/secure-exams/${examId}/questions/${qid}`, { headers });
      setExam(e => ({ ...e, questions: e.questions.filter(x => x.question_id !== qid) }));
    } catch (e) { toast.error("Delete failed"); }
  };

  const uploadImage = async (file) => {
    if (!file) return null;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/files/upload`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      return r.data.url;
    } catch (e) {
      toast.error("Image upload failed");
      return null;
    }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading...</div>;

  const qsBySection = exam.sections.map((_, si) => (exam.questions || []).filter(q => q.section_index === si));

  return (
    <div className="min-h-screen bg-background pb-12">
      <div className="border-b border-border bg-slate-950 text-white">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/secure-exams")} className="text-white hover:bg-slate-800"><ArrowLeft className="w-4 h-4" /></Button>
            <ShieldAlert className="w-6 h-6 text-amber-400" />
            <div>
              <h1 className="text-lg font-bold">{editing ? "Edit Secure Exam" : "New Secure Exam"}</h1>
              <p className="text-[11px] text-slate-400">Status: {exam.status || "draft"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={saveMeta} disabled={saving} data-testid="save-exam-btn" className="bg-white text-slate-900">
              <Save className="w-4 h-4 mr-1" />{saving ? "Saving..." : "Save"}
            </Button>
            {editing && exam.status !== "published" && (
              <Button size="sm" onClick={publish} className="bg-amber-500 hover:bg-amber-400 text-slate-950" data-testid="publish-exam-btn">
                <Send className="w-4 h-4 mr-1" />Publish
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-5">
        <Tabs defaultValue="details">
          <TabsList>
            <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
            <TabsTrigger value="sections" data-testid="tab-sections">Sections</TabsTrigger>
            <TabsTrigger value="instructions" data-testid="tab-instructions">Instructions</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Lockdown & Rules</TabsTrigger>
            <TabsTrigger value="questions" disabled={!editing} data-testid="tab-questions">Questions ({(exam.questions || []).length})</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="mt-4 space-y-4">
            <Card className="p-5 space-y-3">
              <div>
                <Label>Title</Label>
                <Input value={exam.title} onChange={(e) => setExam({ ...exam, title: e.target.value })} data-testid="exam-title-input" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={exam.description} onChange={(e) => setExam({ ...exam, description: e.target.value })} data-testid="exam-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Total Duration (minutes)</Label>
                  <Input type="number" min={1} value={exam.duration_minutes} onChange={(e) => setExam({ ...exam, duration_minutes: e.target.value })} data-testid="exam-duration-input" />
                </div>
                <div>
                  <Label>Scheduled Start (optional)</Label>
                  <Input type="datetime-local" value={exam.starts_at ? new Date(exam.starts_at).toISOString().slice(0,16) : ""} onChange={(e) => setExam({ ...exam, starts_at: e.target.value ? new Date(e.target.value).toISOString() : null })} />
                </div>
              </div>
              <div>
                <Label>Assign to Classes</Label>
                <div className="mt-1 space-y-1 max-h-40 overflow-y-auto border border-border rounded-lg p-2">
                  {classes.length === 0 ? <p className="text-xs text-slate-500">No classes yet.</p> : classes.map(c => (
                    <label key={c.class_id} className="flex items-center gap-2 text-sm p-1 hover:bg-muted rounded">
                      <Checkbox
                        checked={exam.class_ids.includes(c.class_id)}
                        onCheckedChange={(v) => setExam(e => ({ ...e, class_ids: v ? [...e.class_ids, c.class_id] : e.class_ids.filter(x => x !== c.class_id) }))}
                        data-testid={`class-cb-${c.class_id}`}
                      />
                      <span>{c.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="sections" className="mt-4 space-y-3">
            {exam.sections.map((s, i) => (
              <Card key={i} className="p-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-bold">{i + 1}</div>
                <Input value={s.name} onChange={(e) => updateSection(i, "name", e.target.value)} className="flex-1" data-testid={`section-name-${i}`} />
                <div className="flex items-center gap-1">
                  <Input type="number" min={30} value={s.duration_seconds} onChange={(e) => updateSection(i, "duration_seconds", Number(e.target.value))} className="w-24" data-testid={`section-duration-${i}`} />
                  <span className="text-xs text-slate-500">sec</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeSection(i)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
              </Card>
            ))}
            <Button variant="outline" onClick={addSection} data-testid="add-section-btn"><Plus className="w-4 h-4 mr-1" />Add Section</Button>
          </TabsContent>

          <TabsContent value="instructions" className="mt-4 space-y-4">
            <Card className="p-5 space-y-2">
              <Label>General Instructions</Label>
              <p className="text-xs text-slate-500">Shown to students on the first screen before the exam. One instruction per line. Pre-filled with the standard Olympiad instructions — edit freely.</p>
              <Textarea
                rows={16}
                value={exam.general_instructions_text}
                onChange={(e) => setExam({ ...exam, general_instructions_text: e.target.value })}
                data-testid="general-instructions-input"
                className="text-sm"
              />
              <Button variant="outline" size="sm" onClick={() => setExam({ ...exam, general_instructions_text: DEFAULT_GENERAL_INSTRUCTIONS.join("\n") })} data-testid="reset-general-instructions-btn">
                Reset to Default
              </Button>
            </Card>
            <Card className="p-5 space-y-2">
              <Label>Test Instructions</Label>
              <p className="text-xs text-slate-500">Shown on the second screen (with sections summary), e.g. "There are 100 questions in the test paper." One per line. <strong>Leave blank to auto-generate</strong> from question count, sections, and marks.</p>
              <Textarea
                rows={5}
                value={exam.test_instructions_text}
                onChange={(e) => setExam({ ...exam, test_instructions_text: e.target.value })}
                placeholder={"Leave blank to auto-generate, e.g.:\nThere are 100 questions in the test paper.\nPaper is divided into 3 sections.\nAttempt all the questions.\nAll questions carry equal marks."}
                data-testid="test-instructions-input"
                className="text-sm"
              />
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="mt-4 space-y-3">
            <Card className="p-5 space-y-3">
              {[
                ["fullscreen_required", "Force Fullscreen"],
                ["face_detection_enabled", "Face Detection (webcam)"],
                ["mic_spike_enabled", "Mic Spike Detection"],
                ["randomize_questions", "Randomize Question Order"],
                ["randomize_options", "Shuffle MCQ Options"],
                ["allow_section_jump", "Allow Section Jumping (move freely between sections)"],
                ["negative_marking", "Enable Negative Marking"],
                ["allow_calculator", "Allow In-App Calculator"]
              ].map(([key, label]) => (
                <label key={key} className="flex items-center justify-between p-2 hover:bg-muted rounded">
                  <span className="text-sm">{label}</span>
                  <Checkbox checked={!!exam.settings[key]} onCheckedChange={(v) => setExam(e => ({ ...e, settings: { ...e.settings, [key]: !!v } }))} data-testid={`setting-${key}`} />
                </label>
              ))}
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div>
                  <Label>Tab-switch strike limit</Label>
                  <Input type="number" min={1} value={exam.settings.tab_switch_limit} onChange={(e) => setExam(x => ({ ...x, settings: { ...x.settings, tab_switch_limit: Number(e.target.value) } }))} />
                </div>
                <div>
                  <Label>Negative marks per wrong Q</Label>
                  <Input type="number" step="0.25" value={exam.settings.negative_marks} onChange={(e) => setExam(x => ({ ...x, settings: { ...x.settings, negative_marks: Number(e.target.value) } }))} />
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="questions" className="mt-4 space-y-4">
            {exam.sections.map((sec, si) => (
              <Card key={si} className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-sm">Section {si + 1}: {sec.name}</h3>
                  <Button size="sm" variant="outline" onClick={() => { setEditingQuestion({ section_index: si, type: "mcq", text: "", options: ["", "", "", ""], correct_answer: "0", numerical_tolerance: 0.01, marks: 4, image_url: "", solution: "" }); setShowQuestionDialog(true); }} data-testid={`add-q-${si}`}>
                    <Plus className="w-3.5 h-3.5 mr-1" />Add Question
                  </Button>
                </div>
                {qsBySection[si].length === 0 ? <p className="text-xs text-slate-500">No questions yet.</p> : (
                  <div className="space-y-2">
                    {qsBySection[si].map((q, i) => (
                      <div key={q.question_id} className="border border-border rounded-lg p-3 flex items-start gap-3" data-testid={`q-row-${q.question_id}`}>
                        <span className="text-xs font-semibold text-slate-500 mt-0.5">Q{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm line-clamp-2">{q.text}</p>
                          <p className="text-[10px] text-slate-500 mt-0.5">{q.type.toUpperCase()} · {q.marks} marks · Correct: {q.correct_answer}</p>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => openQDialog(q)}>Edit</Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteQuestion(q.question_id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </div>

      {/* Question Dialog */}
      <Dialog open={showQuestionDialog} onOpenChange={setShowQuestionDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingQuestion?.question_id ? "Edit Question" : "Add Question"}</DialogTitle>
          </DialogHeader>
          {editingQuestion && (
            <div className="space-y-3">
              <div>
                <Label>Section</Label>
                <select className="w-full h-10 rounded border border-border px-3 bg-background" value={editingQuestion.section_index} onChange={(e) => setEditingQuestion({ ...editingQuestion, section_index: Number(e.target.value) })} data-testid="q-section-select">
                  {exam.sections.map((s, i) => <option key={i} value={i}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Type</Label>
                <select className="w-full h-10 rounded border border-border px-3 bg-background" value={editingQuestion.type} onChange={(e) => setEditingQuestion({ ...editingQuestion, type: e.target.value })} data-testid="q-type-select">
                  <option value="mcq">Multiple Choice</option>
                  <option value="numerical">Numerical Answer</option>
                </select>
              </div>
              <div>
                <Label>Question (supports LaTeX: $x^2$ or $$\frac{"{a}"}{"{b}"}$$)</Label>
                <Textarea value={editingQuestion.text} onChange={(e) => setEditingQuestion({ ...editingQuestion, text: e.target.value })} rows={3} data-testid="q-text-input" />
              </div>
              <div>
                <Label>Image (optional)</Label>
                <div className="flex items-center gap-2">
                  <Input type="file" accept="image/*" onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      const url = await uploadImage(f);
                      if (url) setEditingQuestion({ ...editingQuestion, image_url: url });
                    }
                  }} />
                  {editingQuestion.image_url && <img src={editingQuestion.image_url} alt="preview" className="h-12 w-12 object-cover rounded" />}
                </div>
              </div>
              {editingQuestion.type === "mcq" ? (
                <div>
                  <Label>Options (mark the correct one)</Label>
                  {editingQuestion.options.map((opt, i) => (
                    <div key={i} className="flex items-center gap-2 mt-1.5">
                      <input type="radio" name="correct" checked={editingQuestion.correct_answer === String(i)} onChange={() => setEditingQuestion({ ...editingQuestion, correct_answer: String(i) })} data-testid={`q-correct-${i}`} />
                      <Input value={opt} onChange={(e) => { const nx = [...editingQuestion.options]; nx[i] = e.target.value; setEditingQuestion({ ...editingQuestion, options: nx }); }} placeholder={`Option ${i + 1}`} data-testid={`q-opt-${i}`} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Correct Answer (numeric)</Label>
                    <Input value={editingQuestion.correct_answer} onChange={(e) => setEditingQuestion({ ...editingQuestion, correct_answer: e.target.value })} data-testid="q-num-answer" />
                  </div>
                  <div>
                    <Label>Tolerance (±)</Label>
                    <Input type="number" step="0.01" value={editingQuestion.numerical_tolerance} onChange={(e) => setEditingQuestion({ ...editingQuestion, numerical_tolerance: e.target.value })} />
                  </div>
                </div>
              )}
              <div>
                <Label>Marks</Label>
                <Input type="number" step="0.5" value={editingQuestion.marks} onChange={(e) => setEditingQuestion({ ...editingQuestion, marks: e.target.value })} data-testid="q-marks-input" />
              </div>
              <div>
                <Label>Solution / Explanation (optional)</Label>
                <Textarea
                  value={editingQuestion.solution || ""}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, solution: e.target.value })}
                  rows={3}
                  placeholder="Step-by-step explanation shown to students after the exam (when solutions are released or in practice mode). Supports LaTeX like $x^2$."
                  data-testid="q-solution-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuestionDialog(false)}>Cancel</Button>
            <Button onClick={saveQuestion} data-testid="save-question-btn">Save Question</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
